import { __decorate, __metadata } from "tslib";
// NPM
import { Component, Mixins, Watch } from "vue-property-decorator";
import { interpret } from "xstate";
// S1
import BaseMixer from "@/router/views/BaseMixer";
import Layout from "../../../layouts/main.vue";
import PageHeader from "@/components/page-header.vue";
import EcGrid from "@/components/widgets/EcGrid.vue";
import DeleteComponent from "@/components/deleteComponent.vue";
import ECAlert from "@/components/ECAlert.vue";
import { ActionItemStatus } from "@/store/models/clientUpdates/actionItem";
import ClientUpdatesModule from "@/store/modules/clientUpdates.module";
import actionItemsMachine from "./machines/ActionItemsMachine";
import AddActionItemModal from "./AddActionItemModal.vue";
import { DateFormatConstant } from "@/store/DateFormatConstants";
import { canModifyActionItem, DEACTIVATED_CLIENT_HINT, formatDate, formatUpdateHtml, getActionItemDisplayStatus, getInitials, isClientReadOnlyItem, NOT_OWNER_OR_ASSIGNEE_HINT, NOTES_LABEL, parseDate, sameId, UPDATE_SIDE_DEVELOPMENT_ID, } from "../updatesHelpers";
// Server values for the Open tab's Due by filter ("All" is the empty placeholder)
const DUE_BY_OPTIONS = [
    { value: "today", text: "Today" },
    { value: "thisWeek", text: "This week" },
    { value: "overdue", text: "Overdue" },
];
const DEFAULT_PAGE_SIZE = 10;
// Team filter cell - disabled (shows "All") until a client is picked in the Client filter
const TEAM_FILTER_CLASS = "action-item-team-filter";
const TEAM_FILTER_DISABLED_CLASS = TEAM_FILTER_CLASS + " is-disabled";
// vue-good-table draws a filter with no items as a text box - the disabled Team filter keeps one "All" item
const TEAM_FILTER_EMPTY_ITEMS = [{ value: "", text: "All" }];
// The item's own team, or its update's team - the same column on every tab, filtered by team id like Client
const teamColumn = () => ({
    field: "teamName",
    label: "Team",
    tdClass: "action-item-team",
    thClass: "action-item-team-header",
    sortable: true,
    filterOptions: {
        enabled: true,
        placeholder: "All",
        filterDropdownItems: TEAM_FILTER_EMPTY_ITEMS,
        styleClass: TEAM_FILTER_DISABLED_CLASS,
    },
});
// Active-tab complete: tint the row, then fade it out before it leaves the list (design timing)
const COMPLETE_TINT_MS = 540;
const COMPLETE_LEAVE_MS = 880;
// View Update popover size / space needed below its button before it flips above
const UPDATE_POP_WIDTH = 560;
const UPDATE_POP_MIN_SPACE = 320;
let ActionItems = /** @class */ (() => {
    let ActionItems = class ActionItems extends Mixins(BaseMixer) {
        constructor() {
            super(...arguments);
            this.machine = actionItemsMachine.withContext({
                ...actionItemsMachine.context,
                searchData: { page: 1, pageSize: DEFAULT_PAGE_SIZE, sort: null, columnFilters: [], status: ActionItemStatus.Open },
            });
            this.machineService = interpret(this.machine, { devTools: true });
            this.state = this.machine.initialState;
            this.title = "Action Items";
            this.items = [
                {
                    text: "Home",
                    to: "/dashBoard",
                },
                {
                    text: "Client Success",
                },
                {
                    text: "Action Items",
                    active: true,
                },
            ];
            this.tabs = [
                { key: "open", label: "Active" },
                { key: "completed", label: "Completed" },
                { key: "closed", label: "Closed" },
            ];
            // Grids - one fixed column set per tab, each tab rendering its own EcGrid (Review listing pattern).
            // Widths come from the td/th classes (style block); dropdown items are filled in after each search.
            // vue-good-table max-height of the scrollable table, measured to the card so the page itself does not scroll
            this.gridMaxHeight = null;
            // Active tab: Due by is filterable
            this.activeColumns = [
                {
                    field: "description",
                    label: "Action Item",
                    tdClass: "action-item-name",
                    thClass: "action-item-name-header",
                    sortable: true,
                },
                {
                    field: "clientName",
                    label: "Client",
                    tdClass: "action-item-client",
                    thClass: "action-item-client-header",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "All",
                        filterDropdownItems: [],
                        styleClass: "action-item-client-filter",
                    },
                },
                teamColumn(),
                {
                    field: "assigneeName",
                    label: "Assignee",
                    tdClass: "action-item-assignee",
                    thClass: "action-item-assignee-header",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "All",
                        filterDropdownItems: [],
                        styleClass: "action-item-assignee-filter",
                    },
                },
                {
                    field: "dueDate",
                    label: "Due by",
                    tdClass: "action-item-due",
                    thClass: "action-item-due-header",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "All",
                        filterDropdownItems: DUE_BY_OPTIONS,
                        styleClass: "action-item-due-filter",
                    },
                },
                {
                    field: "createdByName",
                    label: "Created by",
                    tdClass: "action-item-creator",
                    thClass: "action-item-creator-header",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "All",
                        filterDropdownItems: [],
                        styleClass: "action-item-creator-filter",
                    },
                },
                {
                    field: "createdOn",
                    label: "Created on",
                    tdClass: "action-item-created",
                    thClass: "action-item-created-header",
                    sortable: true,
                },
                {
                    field: "actions",
                    label: "Actions",
                    tdClass: "action-item-actions",
                    thClass: "text-center action-item-actions-header",
                    sortable: false,
                },
            ];
            // Completed tab: Due by shows the original due date without a filter, and Completed on is added.
            // No Actions column - a completed item can't be edited or deleted (reopen it first).
            this.completedColumns = [
                {
                    field: "description",
                    label: "Action Item",
                    tdClass: "action-item-name",
                    thClass: "action-item-name-header",
                    sortable: true,
                },
                {
                    field: "clientName",
                    label: "Client",
                    tdClass: "action-item-client",
                    thClass: "action-item-client-header",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "All",
                        filterDropdownItems: [],
                        styleClass: "action-item-client-filter",
                    },
                },
                teamColumn(),
                {
                    field: "assigneeName",
                    label: "Assignee",
                    tdClass: "action-item-assignee",
                    thClass: "action-item-assignee-header",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "All",
                        filterDropdownItems: [],
                        styleClass: "action-item-assignee-filter",
                    },
                },
                {
                    field: "dueDate",
                    label: "Due by",
                    tdClass: "action-item-due",
                    thClass: "action-item-due-header",
                    sortable: true,
                },
                {
                    field: "completedOn",
                    label: "Completed on",
                    tdClass: "action-item-completed",
                    thClass: "action-item-completed-header",
                    sortable: true,
                },
                {
                    field: "createdByName",
                    label: "Created by",
                    tdClass: "action-item-creator",
                    thClass: "action-item-creator-header",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "All",
                        filterDropdownItems: [],
                        styleClass: "action-item-creator-filter",
                    },
                },
                {
                    field: "createdOn",
                    label: "Created on",
                    tdClass: "action-item-created",
                    thClass: "action-item-created-header",
                    sortable: true,
                },
            ];
            // Closed tab: like Completed, with Closed on / Closed by instead of Completed on, and a Reopen action
            this.closedColumns = [
                {
                    field: "description",
                    label: "Action Item",
                    tdClass: "action-item-name",
                    thClass: "action-item-name-header",
                    sortable: true,
                },
                {
                    field: "clientName",
                    label: "Client",
                    tdClass: "action-item-client",
                    thClass: "action-item-client-header",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "All",
                        filterDropdownItems: [],
                        styleClass: "action-item-client-filter",
                    },
                },
                teamColumn(),
                {
                    field: "assigneeName",
                    label: "Assignee",
                    tdClass: "action-item-assignee",
                    thClass: "action-item-assignee-header",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "All",
                        filterDropdownItems: [],
                        styleClass: "action-item-assignee-filter",
                    },
                },
                {
                    field: "dueDate",
                    label: "Due by",
                    tdClass: "action-item-due",
                    thClass: "action-item-due-header",
                    sortable: true,
                },
                {
                    field: "closedOn",
                    label: "Closed on",
                    tdClass: "action-item-closed",
                    thClass: "action-item-closed-header",
                    sortable: true,
                },
                {
                    field: "closedByName",
                    label: "Closed by",
                    tdClass: "action-item-closer",
                    thClass: "action-item-closer-header",
                    sortable: true,
                },
                {
                    field: "createdByName",
                    label: "Created by",
                    tdClass: "action-item-creator",
                    thClass: "action-item-creator-header",
                    sortable: true,
                    filterOptions: {
                        enabled: true,
                        placeholder: "All",
                        filterDropdownItems: [],
                        styleClass: "action-item-creator-filter",
                    },
                },
                {
                    field: "actions",
                    label: "Actions",
                    tdClass: "action-item-actions",
                    thClass: "text-center action-item-actions-header",
                    sortable: false,
                },
            ];
            // Grid column filters of the visible grid as vue-good-table reports them: { field: value }
            this.columnFilterValues = {};
            // Modal / confirm targets
            this.editingActionItem = null;
            this.pendingDeleteId = null;
            this.pendingCloseId = null;
            // The empty state waits for the first search so it doesn't flash before the grid has loaded
            this.initialLoadDone = false;
            // Rows checked on the Active tab stay put while they tint and fade, even after the server drops them
            this.leavePhase = {};
            this.leaveHold = {};
            this.leaveTimers = {};
            // Screen position of the "View Update" button the popover opens from
            this.updatePopAnchor = null;
            // The open update's author photo failed to load - show initials instead
            this.updateAuthorImageBroken = false;
            this.formatDate = formatDate;
            this.formatUpdateHtml = formatUpdateHtml;
            this.getInitials = getInitials;
            this.notOwnerHint = NOT_OWNER_OR_ASSIGNEE_HINT;
            this.notesLabel = NOTES_LABEL;
            // vue-good-table calls this with the row; an arrow keeps `this` on the page
            this.rowClass = (row) => {
                const phase = this.leavePhase[this.key(row)];
                if (phase === "out") {
                    return "act-row-done act-row-leaving";
                }
                if (phase === "tint") {
                    return "act-row-done";
                }
                return "";
            };
        }
        send(event) {
            this.machineService.send(event);
        }
        // Store
        get rows() {
            return ClientUpdatesModule.actionItemGrid;
        }
        get totalCount() {
            return ClientUpdatesModule.actionItemTotalCount;
        }
        get filterOptions() {
            return ClientUpdatesModule.actionItemFilterOptions;
        }
        // New action items can only be filed against active clients
        get clientOptions() {
            return (ClientUpdatesModule.clientOptions || []).filter((client) => client.isActive !== false);
        }
        get updateSummary() {
            return ClientUpdatesModule.updateSummary;
        }
        // View state
        get activeTab() {
            return this.state.context.activeTab;
        }
        get searchData() {
            return this.state.context.searchData;
        }
        get isGridLoading() {
            return this.state.matches("gridInfo.gettingActionItems") || this.state.matches("toggleInfo.togglingActionItem") || this.state.matches("closeInfo.closingActionItem");
        }
        get hasLeavingRow() {
            return Object.keys(this.leavePhase).length > 0;
        }
        // The table loader would cover the fade, so keep the rows visible until the animation finishes
        get showGridLoader() {
            return this.isGridLoading && !this.hasLeavingRow;
        }
        get isFilterApplied() {
            return Object.keys(this.columnFilterValues).some((field) => !!this.columnFilterValues[field]);
        }
        // Departing rows are spliced back in if the refresh already removed them
        get gridRows() {
            const source = this.rows || [];
            if (this.activeTab !== "open" || !this.hasLeavingRow) {
                return source;
            }
            const rows = source.slice();
            const present = new Set(rows.map((row) => this.key(row)));
            Object.keys(this.leaveHold).forEach((id) => {
                if (!this.leavePhase[id] || present.has(id)) {
                    return;
                }
                const hold = this.leaveHold[id];
                rows.splice(Math.max(0, Math.min(hold.index, rows.length)), 0, hold.row);
            });
            return rows;
        }
        get gridTotalCount() {
            if (this.activeTab !== "open") {
                return this.totalCount;
            }
            const present = new Set((this.rows || []).map((row) => this.key(row)));
            const extra = Object.keys(this.leaveHold).filter((id) => !!this.leavePhase[id] && !present.has(id)).length;
            return this.totalCount + extra;
        }
        // Active has no rows: replace the grid with the add prompt. Completed keeps the table and uses its empty row.
        get showEmptyState() {
            return this.activeTab === "open" && this.initialLoadDone && !this.isGridLoading && !this.hasLeavingRow && this.gridRows.length === 0 && !this.isFilterApplied;
        }
        get emptyNote() {
            return "No active action items found.";
        }
        get emptyGridMessage() {
            if (this.activeTab === "completed") {
                return "No completed action items yet.";
            }
            return this.activeTab === "closed" ? "No closed action items." : "No records found.";
        }
        // Nothing to clear when the Completed / Closed list itself is empty. A filter that matches nothing still needs the button.
        get showClearFilter() {
            const finishedListEmpty = this.activeTab !== "open" && this.initialLoadDone && !this.isGridLoading && this.gridRows.length === 0 && !this.isFilterApplied;
            return !finishedListEmpty;
        }
        // New items belong on the Active list only
        get canAddActionItem() {
            return this.activeTab === "open";
        }
        get showActionItemModal() {
            return this.state.matches("actionItemInfo.actionItemFormOpen") || this.state.matches("actionItemInfo.savingActionItem");
        }
        get showUpdatePop() {
            return this.state.matches("updatePopInfo.updateOpen") && !!this.updateSummary;
        }
        get isUpdateFromDevelopment() {
            return !!this.updateSummary && sameId(this.updateSummary.updateSideId, UPDATE_SIDE_DEVELOPMENT_ID);
        }
        // Rows
        key(row) {
            return row.id.toString();
        }
        isCompleted(row) {
            return row.status === ActionItemStatus.Completed;
        }
        isClosed(row) {
            return row.status === ActionItemStatus.Closed;
        }
        // Owner or assignee only, and never for a deactivated client's item (the API enforces both)
        canModify(row) {
            return !isClientReadOnlyItem(row) && canModifyActionItem(row);
        }
        // Why a row's tick / Edit / Delete is disabled
        lockedHint(row) {
            return isClientReadOnlyItem(row) ? DEACTIVATED_CLIENT_HINT : NOT_OWNER_OR_ASSIGNEE_HINT;
        }
        isOverdue(row) {
            return getActionItemDisplayStatus(row) === "Overdue";
        }
        // Grid columns
        toFilterItems(items) {
            return (items || []).map((x) => ({ value: x.id.toString(), text: x.value }));
        }
        // One grid per tab, each with its own fixed column set - only the active tab's grid is rendered, and the
        // tab key gives each tab its own grid instance
        get visibleGrids() {
            const grids = [
                { key: "open", columns: this.activeColumns },
                { key: "completed", columns: this.completedColumns },
                { key: "closed", columns: this.closedColumns },
            ];
            return grids.filter((grid) => grid.key === this.activeTab);
        }
        // Same as the Client list: the user's date format, "-" when empty
        formatGridDate(value) {
            const date = parseDate(value);
            if (!date) {
                return "-";
            }
            return this.dateFormat === DateFormatConstant.DateFormat.DateFormatMDY() ? this.dateFormattedString(date) : this.dateFormatted(date);
        }
        // The Team filter lists the picked client's teams, so it stays disabled (showing "All") until there are some
        get isTeamFilterDisabled() {
            return !this.columnFilterValues.clientName || !(this.filterOptions.teams || []).length;
        }
        // Filter options come back with every search - only touch the columns when they actually changed
        syncFilterOptions() {
            const teamDisabled = this.isTeamFilterDisabled;
            const byField = {
                clientName: this.toFilterItems(this.filterOptions.clients),
                teamName: teamDisabled ? TEAM_FILTER_EMPTY_ITEMS : this.toFilterItems(this.filterOptions.teams),
                assigneeName: this.toFilterItems(this.filterOptions.assignees),
                createdByName: this.toFilterItems(this.filterOptions.creators),
            };
            const teamStyleClass = teamDisabled ? TEAM_FILTER_DISABLED_CLASS : TEAM_FILTER_CLASS;
            this.activeColumns.concat(this.completedColumns, this.closedColumns).forEach((column) => {
                const items = byField[column.field];
                if (items && column.filterOptions && JSON.stringify(column.filterOptions.filterDropdownItems) !== JSON.stringify(items)) {
                    column.filterOptions.filterDropdownItems = items;
                }
                if (column.field === "teamName" && column.filterOptions.styleClass !== teamStyleClass) {
                    column.filterOptions.styleClass = teamStyleClass;
                }
            });
        }
        // Search
        toFilterList(values) {
            return Object.keys(values)
                .filter((field) => !!values[field])
                .map((field) => ({ name: field, value: values[field] }));
        }
        search(changes) {
            this.send({ type: "SEARCH", searchData: { ...this.searchData, ...changes } });
        }
        onPageChange(params) {
            if (params.currentPage !== this.searchData.page) {
                this.search({ page: params.currentPage });
            }
        }
        onPerPageChange(params) {
            this.search({ page: 1, pageSize: params.currentPerPage });
        }
        onSortChange(params) {
            const sort = params && params[0] ? { field: params[0].field, type: params[0].type } : null;
            this.search({ page: 1, sort: sort });
        }
        onColumnFilter(params) {
            const values = { ...((params && params.columnFilters) || {}) };
            // vue-good-table re-reports unchanged filters whenever the columns change - only search on a real change
            if (JSON.stringify(this.toFilterList(values)) === JSON.stringify(this.toFilterList(this.columnFilterValues))) {
                return;
            }
            // Team options are the picked client's teams, so a client change drops the picked team in the same search
            if ((values.clientName || "") !== (this.columnFilterValues.clientName || "") && values.teamName) {
                values.teamName = "";
                const filterRow = this.gridFilterRow();
                if (filterRow) {
                    this.$set(filterRow.columnFilters, "teamName", "");
                }
            }
            this.columnFilterValues = values;
            this.search({ page: 1, columnFilters: this.toFilterList(values) });
        }
        onRowClick() { }
        // Tabs
        // Each tab mounts a fresh grid, so it starts with no filters, the default sort and page 1 (Review listing
        // behaviour). Locked while the grid is loading.
        selectTab(tab) {
            if (tab === this.activeTab || this.isGridLoading || this.hasLeavingRow) {
                return;
            }
            this.columnFilterValues = {};
            this.send({ type: "SELECT_TAB", tab: tab, searchData: { ...this.searchData, page: 1, sort: null, columnFilters: [] } });
        }
        // The visible tab's grid (refs inside v-for come back as arrays)
        gridFilterRow() {
            const ref = this.$refs["grid-" + this.activeTab];
            const ecgrid = Array.isArray(ref) ? ref[0] : ref;
            return ecgrid && ecgrid.$refs.grid && ecgrid.$refs.grid.$refs["table-header-primary"] ? ecgrid.$refs.grid.$refs["table-header-primary"].$refs["filter-row"] : null;
        }
        // Same as the Employees / Clients lists: reset the grid's filter row and reload
        clearFilter() {
            const filterRow = this.gridFilterRow();
            if (filterRow) {
                filterRow.columnFilters = {};
            }
            this.columnFilterValues = {};
            this.search({ page: 1, columnFilters: [] });
        }
        // Checkbox
        showsCompleted(row) {
            return this.isCompleted(row) || !!this.leavePhase[this.key(row)];
        }
        toggle(row) {
            const id = this.key(row);
            if (this.leavePhase[id] || this.isGridLoading) {
                return;
            }
            // Completing from Active plays the tint-then-fade before the row leaves the list
            if (this.activeTab === "open" && !this.isCompleted(row)) {
                const index = (this.rows || []).findIndex((item) => this.key(item) === id);
                this.$set(this.leavePhase, id, "tint");
                this.$set(this.leaveHold, id, {
                    row: { ...row, status: ActionItemStatus.Completed },
                    index: Math.max(index, 0),
                });
                this.leaveTimers[id] = [
                    window.setTimeout(() => {
                        if (this.leavePhase[id] === "tint") {
                            this.$set(this.leavePhase, id, "out");
                        }
                    }, COMPLETE_TINT_MS),
                    window.setTimeout(() => this.finishLeave(id), COMPLETE_LEAVE_MS),
                ];
            }
            this.send({ type: "TOGGLE_ACTION_ITEM", payLoad: row });
        }
        // Drop the row once it has faded and the server list no longer has it as open
        finishLeave(id) {
            if (!this.leavePhase[id]) {
                return;
            }
            if (this.isGridLoading) {
                const timer = window.setTimeout(() => this.finishLeave(id), 120);
                this.leaveTimers[id] = (this.leaveTimers[id] || []).concat(timer);
                return;
            }
            this.clearLeave(id);
        }
        clearLeave(id) {
            (this.leaveTimers[id] || []).forEach((timer) => window.clearTimeout(timer));
            delete this.leaveTimers[id];
            if (this.leavePhase[id]) {
                this.$delete(this.leavePhase, id);
            }
            if (this.leaveHold[id]) {
                this.$delete(this.leaveHold, id);
            }
        }
        clearAllLeaves() {
            Object.keys(this.leaveTimers).forEach((id) => this.clearLeave(id));
        }
        // Add / Edit modal
        openAddActionItem() {
            if (!this.canAddActionItem) {
                return;
            }
            this.editingActionItem = null;
            this.send({ type: "ADD_ACTION_ITEM" });
        }
        openEditActionItem(row) {
            this.editingActionItem = row;
            this.send({ type: "EDIT_ACTION_ITEM" });
        }
        saveActionItem(model) {
            this.send({ type: "SAVE_ACTION_ITEM", payLoad: model });
        }
        closeActionItemModal() {
            this.send({ type: "CLOSE_ACTION_ITEM_MODAL" });
        }
        // Delete
        requestDelete(row) {
            this.pendingDeleteId = this.key(row);
            this.send({ type: "DELETE_ACTION_ITEM" });
        }
        confirmDelete() {
            this.send({ type: "DELETING_ACTION_ITEM", payLoad: this.pendingDeleteId });
        }
        cancelDelete() {
            this.pendingDeleteId = null;
            this.send({ type: "CLOSE_MODAL" });
        }
        // Close (Active tab) - no longer needed, so it won't be done; Reopen brings it back
        requestClose(row) {
            this.pendingCloseId = this.key(row);
            this.send({ type: "CLOSE_ACTION_ITEM" });
        }
        confirmClose() {
            this.send({ type: "CLOSING_ACTION_ITEM", payLoad: this.pendingCloseId });
        }
        cancelClose() {
            this.pendingCloseId = null;
            this.send({ type: "CANCEL_CLOSE_ACTION_ITEM" });
        }
        // Reopen (Closed tab) - back to the Active list
        reopen(row) {
            if (this.isGridLoading) {
                return;
            }
            this.send({ type: "TOGGLE_ACTION_ITEM", payLoad: row });
        }
        // View Update popover - anchored to the clicked "View Update" button
        viewUpdate(row, event) {
            if (!row.updateId) {
                return;
            }
            const target = event && event.currentTarget;
            const rect = target ? target.getBoundingClientRect() : null;
            this.updatePopAnchor = rect ? { left: rect.left, top: rect.top, bottom: rect.bottom } : null;
            this.updateAuthorImageBroken = false;
            this.send({ type: "VIEW_UPDATE", updateId: row.updateId });
        }
        // Author photo in the View Update popover - falls back to initials when missing or it fails to load
        get showUpdateAuthorImage() {
            return !!this.updateSummary && !!this.updateSummary.createdByImageURL && !this.updateAuthorImageBroken;
        }
        closeUpdate() {
            this.send({ type: "CLOSE_UPDATE" });
        }
        // Opens below the button (above it when there isn't room), left-aligned with it and kept inside the viewport
        get updatePopStyle() {
            const anchor = this.updatePopAnchor;
            if (!anchor) {
                return {};
            }
            const gap = 6;
            const margin = 16;
            const width = Math.min(UPDATE_POP_WIDTH, window.innerWidth - margin * 2);
            const left = Math.max(margin, Math.min(anchor.left, window.innerWidth - width - margin));
            const openAbove = window.innerHeight - anchor.bottom < UPDATE_POP_MIN_SPACE && anchor.top > window.innerHeight - anchor.bottom;
            return {
                left: left + "px",
                top: (openAbove ? anchor.top - gap : anchor.bottom + gap) + "px",
                width: width + "px",
                transform: openAbove ? "translateY(-100%)" : "none",
            };
        }
        // Fit the vue-good-table scrollport to the card: body height minus the pager, which stays put under the table
        measureGridHeight() {
            const body = this.$refs.cardBody;
            if (!body) {
                return;
            }
            const pagination = body.querySelector(".ec-pagination");
            const bar = pagination ? pagination.closest(".p-3") : null;
            const available = body.clientHeight - (bar ? bar.offsetHeight : 0);
            if (available < 120) {
                return;
            }
            const current = this.gridMaxHeight ? parseInt(this.gridMaxHeight, 10) : 0;
            if (Math.abs(available - current) < 2) {
                return;
            }
            this.gridMaxHeight = available + "px";
        }
        observeGridHeight() {
            this.$nextTick(() => {
                this.measureGridHeight();
                window.requestAnimationFrame(() => this.measureGridHeight());
            });
        }
        onGridLayoutChanged() {
            this.$nextTick(() => this.measureGridHeight());
        }
        // A fixed popover would drift away from its button - close it instead
        onViewportChange(event) {
            if (!event || event.type === "resize") {
                this.measureGridHeight();
            }
            // Scrolling the popover's own text is fine
            const pop = this.$refs.updatePop;
            if (event && pop && event.target instanceof Node && pop.contains(event.target)) {
                return;
            }
            if (this.state.matches("updatePopInfo.updateOpen") || this.state.matches("updatePopInfo.gettingUpdate")) {
                this.closeUpdate();
            }
        }
        created() {
            // Start clean - a late response from a previous visit may have written to the store after it closed
            ClientUpdatesModule.ResetState();
            this.machineService.onTransition((state) => {
                const previousState = this.state;
                this.state = state;
                if (state.matches("gridInfo.loadedActionItems") && previousState.matches("gridInfo.gettingActionItems")) {
                    this.syncFilterOptions();
                    this.$nextTick(() => this.measureGridHeight());
                }
                if (previousState.matches("toggleInfo.togglingActionItem") && !state.matches("toggleInfo.togglingActionItem")) {
                    const failed = state.event && typeof state.event.type === "string" && state.event.type.indexOf("error") === 0;
                    if (failed) {
                        this.clearAllLeaves();
                    }
                }
                if (!this.initialLoadDone && !state.matches("gridInfo.gettingActionItems")) {
                    this.initialLoadDone = true;
                }
            });
            this.machineService.start();
        }
        mounted() {
            window.addEventListener("scroll", this.onViewportChange, true);
            window.addEventListener("resize", this.onViewportChange);
            this.observeGridHeight();
        }
        beforeDestroy() {
            window.removeEventListener("scroll", this.onViewportChange, true);
            window.removeEventListener("resize", this.onViewportChange);
            this.clearAllLeaves();
            this.machineService.stop();
            ClientUpdatesModule.ResetState();
        }
    };
    __decorate([
        Watch("columnFilterValues"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], ActionItems.prototype, "syncFilterOptions", null);
    __decorate([
        Watch("activeTab"),
        Watch("showEmptyState"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], ActionItems.prototype, "onGridLayoutChanged", null);
    ActionItems = __decorate([
        Component({
            name: "ActionItems",
            components: {
                Layout,
                PageHeader,
                EcGrid,
                DeleteComponent,
                ECAlert,
                AddActionItemModal,
            },
        })
    ], ActionItems);
    return ActionItems;
})();
export default ActionItems;
